// Which band a band control is showing and editing (Sep 2026, user: "if you
// change a setting like a drum loop but By section is selected it won't
// change"). With the band split by section or by written feel, a section with
// a band of its own ignores the tune's band, so a change made to the tune's
// band was silent there. The controls now say which band they are showing (All,
// or one label) and write to it; this module is the pure half of that.
//
// The label that plays the tune's band has no band of its own, so an edit to it
// makes one (a copy of the tune's band with the change). "All" changes the
// tune's band and every label that has a band of its own, field by field, so
// the sections keep what the change does not touch (A's left hand and B's two
// hands survive a change of drum loop). The mixer's on/off switches are never
// scoped: a section cannot turn a channel back on (useBandArrangement's
// resolve), so they are always the tune's.

import { getStylePreset, type StyleBand, type StyleId } from '@/music/style';

/** The scope meaning every label at once. */
export const BAND_SCOPE_ALL = '*all*';

export type BandSplitKind = 'sections' | 'feels';

/** How the band is split, as the band hook sees it. */
export interface BandSplit {
  kind: BandSplitKind;
  /** The labels in form order, each once. */
  labels: readonly string[];
  /** The band a label plays in place of the tune's, if it has one. */
  ownBand: (label: string) => StyleBand | undefined;
}

/** The mixer's switches: the tune's, whatever the scope. */
export const MIXER_FIELDS = [
  'bassline',
  'bassSolo',
  'drums',
] as const satisfies readonly (keyof StyleBand)[];
type MixerField = (typeof MIXER_FIELDS)[number];

const isMixerField = (key: string): key is MixerField =>
  (MIXER_FIELDS as readonly string[]).includes(key);

/** The scope in force: the stored one while it names a label, else All. */
export function resolveBandScope(split: BandSplit | null, stored: string | null): string {
  if (!split || !stored) return BAND_SCOPE_ALL;
  return split.labels.includes(stored) ? stored : BAND_SCOPE_ALL;
}

export interface ShownBand {
  band: StyleBand;
  /** Fields the labels disagree on, under All. */
  varies: ReadonlySet<keyof StyleBand>;
}

const NOTHING_VARIES: ReadonlySet<keyof StyleBand> = new Set();

/** What the controls show for a scope. */
export function shownBand(split: BandSplit | null, scope: string, live: StyleBand): ShownBand {
  if (!split || !split.labels.length) return { band: live, varies: NOTHING_VARIES };
  const withSwitches = (band: StyleBand): StyleBand => ({
    ...band,
    bassline: live.bassline,
    bassSolo: live.bassSolo,
    drums: live.drums,
  });
  if (scope !== BAND_SCOPE_ALL) {
    return { band: withSwitches(split.ownBand(scope) ?? live), varies: NOTHING_VARIES };
  }
  const bands = split.labels.map((label) => split.ownBand(label) ?? live);
  const band = { ...live };
  const varies = new Set<keyof StyleBand>();
  for (const key of Object.keys(live) as (keyof StyleBand)[]) {
    if (isMixerField(key)) continue;
    const first = bands[0][key];
    if (bands.every((b) => b[key] === first)) (band as Record<string, unknown>)[key] = first;
    else varies.add(key);
  }
  return { band, varies };
}

/** What to write for an edit: a patch for the tune's band, and whole bands for labels. */
export interface BandWrites {
  live?: Partial<StyleBand>;
  labels: Record<string, StyleBand>;
}

/** Where an edit made under a scope goes. */
export function scopedBandWrites(
  split: BandSplit | null,
  scope: string,
  live: StyleBand,
  patch: Partial<StyleBand>,
): BandWrites {
  if (!split) return { live: patch, labels: {} };
  const switches: Partial<StyleBand> = {};
  const rest: Partial<StyleBand> = {};
  for (const [key, value] of Object.entries(patch)) {
    (isMixerField(key) ? switches : rest)[key as never] = value as never;
  }
  const labels: Record<string, StyleBand> = {};
  const hasRest = Object.keys(rest).length > 0;
  if (scope === BAND_SCOPE_ALL) {
    if (hasRest) {
      for (const label of split.labels) {
        const own = split.ownBand(label);
        if (own) labels[label] = { ...own, ...rest };
      }
    }
    return { live: patch, labels };
  }
  if (hasRest) labels[scope] = { ...(split.ownBand(scope) ?? live), ...rest };
  return { live: Object.keys(switches).length ? switches : undefined, labels };
}

/**
 * A preset as an edit: its feel, never who plays it or the mixer's switches
 * (the rule applyStyle keeps for the tune's band, applied here to every band a
 * scope reaches).
 */
export function presetPatch(id: StyleId): Partial<StyleBand> | null {
  const preset = getStylePreset(id);
  if (!preset) return null;
  const {
    compInstrument: _instrument,
    compPianoFamily: _family,
    bassline: _bassline,
    bassSolo: _bassSolo,
    drums: _drums,
    ...feel
  } = preset.band;
  return feel;
}

/** How a label reads in a line of text: a letter is a section, a word is itself. */
export function scopeLabelText(label: string): string {
  return /^[A-Z]$/.test(label) ? `Section ${label}` : label;
}
